// useAppUpdate.ts — detects when a newer build has been deployed and exposes a cache-clearing
// reload. Vite emits hash-named bundles, so a new deploy changes the /assets/*.js|css references
// in index.html. We fetch index.html periodically and compare that "asset fingerprint" against the
// one present when this tab loaded; a change means an update is waiting behind a reload.
import { useEffect, useRef, useState } from 'react';

const CHECK_INTERVAL_MS = 60_000;

/** Fingerprint a build from the hashed asset refs in its index.html. */
function assetFingerprint(html: string): string {
  const refs = html.match(/(?:src|href)="[^"]*\/assets\/[^"]+\.(?:js|css)"/g) ?? [];
  return refs.sort().join('|');
}

async function fetchFingerprint(): Promise<string | null> {
  try {
    // Cache-bust + no-store so we always see what the server is serving right now.
    const res = await fetch(`${import.meta.env.BASE_URL}index.html?_=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    return assetFingerprint(await res.text());
  } catch {
    return null;
  }
}

/** Best-effort: drop every browser cache + service worker so the reload pulls a clean build. */
export async function clearCachesAndReload(): Promise<void> {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // best-effort — a plain reload still fetches a fresh index.html (no-cache) and new assets
  }
  window.location.reload();
}

export function useAppUpdate(): {
  updateAvailable: boolean;
  reload: () => Promise<void>;
} {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const baseline = useRef<string | null>(null);

  useEffect(() => {
    // Only meaningful for a deployed build — in dev Vite serves unhashed assets + HMR handles it.
    if (!import.meta.env.PROD) return;
    let cancelled = false;
    let stopped = false;

    const check = async () => {
      if (stopped || cancelled) return;
      const fp = await fetchFingerprint();
      if (cancelled || !fp) return;
      if (baseline.current === null) {
        baseline.current = fp; // first successful read = the build this tab is running
        return;
      }
      if (fp !== baseline.current) {
        stopped = true;
        setUpdateAvailable(true);
      }
    };

    void check();
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    const onWake = () => void check();
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
    };
  }, []);

  return { updateAvailable, reload: clearCachesAndReload };
}
